/**
 * Enviar Home. Guest-first: two large actions (Send / Receive) with no
 * account or friction, plus access to the local history. Minimal and energetic —
 * you come in and send in two taps.
 */

import { createRoute, Link } from "@tanstack/solid-router";
import { Clock, Clock3, Download, ShieldCheck } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { rootRoute } from "@/routes/__root";
import { useI18n } from "@/providers/i18n-provider";
import { PageBackground } from "@/components/ui/page-background";
import { Wordmark } from "@/components/brand/wordmark";
import { SendMark } from "@/components/brand/send-mark";

function HomePage() {
  const { t } = useI18n();
  return (
    <main class="app-screen screen-enter mx-auto w-full max-w-2xl gap-10 lg:max-w-6xl">
      <PageBackground />

      <header class="flex items-center justify-between pt-6">
        <Link
          to="/"
          class="flex items-center transition-transform hover:scale-[1.02] active:scale-95"
        >
          <Wordmark />
        </Link>
        <Link
          to="/history"
          data-testid="link-history"
          class="press inline-flex items-center gap-1.5 rounded-full border border-border bg-surface/60 px-3.5 py-2 text-sm font-medium text-muted-foreground backdrop-blur transition-colors hover:text-foreground"
        >
          <Clock class="h-4 w-4" />
          {t(msg`History`)}
        </Link>
      </header>

      {/*
       * Adaptive by breakpoint (not just scale):
       *  - mobile / tablet: hero centered over the two stacked actions.
       *  - desktop (lg): it reorganizes into TWO columns — hero aligned to the
       *    left and the action cards occupying the right — to
       *    use the width without stretching the text.
       */}
      <div class="flex flex-1 flex-col justify-center gap-10 py-4 lg:grid lg:grid-cols-2 lg:items-center lg:gap-16">
        <div class="flex flex-col items-center gap-4 text-center lg:items-start lg:text-left">
          <span class="inline-flex items-center gap-2 rounded-full border border-accent/25 bg-accent/10 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-accent">
            <span class="relative flex h-2 w-2">
              <span class="animate-wave-ping absolute inline-flex h-full w-full rounded-full bg-accent/70" />
              <span class="relative inline-flex h-2 w-2 rounded-full bg-accent" />
            </span>
            {t(msg`Peer-to-peer · encrypted`)}
          </span>
          <h1 class="font-display text-4xl leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            {t(msg`Send anything, anywhere — instantly.`)}
          </h1>
          <p class="max-w-md text-balance text-base text-muted-foreground lg:text-lg">
            {t(msg`Files between any devices — no account, no cloud, straight from you to them.`)}
          </p>
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
          <Link
            to="/send"
            data-testid="link-send"
            class="press group relative flex flex-col gap-5 overflow-hidden rounded-[var(--radius-2xl)] bg-accent p-6 text-accent-foreground shadow-lg shadow-accent/25 transition-transform duration-200 hover:-translate-y-1 lg:p-7"
          >
            <div
              aria-hidden="true"
              class="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/10 blur-xl"
            />
            <span class="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
              <SendMark class="h-7 w-7" />
            </span>
            <div class="flex flex-col gap-1">
              <span class="font-display text-2xl leading-none">{t(msg`Send`)}</span>
              <span class="text-sm text-accent-foreground/80">
                {t(msg`Pick files, share a code`)}
              </span>
            </div>
          </Link>

          <Link
            to="/receive"
            data-testid="link-receive"
            class="press group relative flex flex-col gap-5 overflow-hidden rounded-[var(--radius-2xl)] border border-border bg-surface/70 p-6 text-foreground shadow-md backdrop-blur transition-transform duration-200 hover:-translate-y-1 lg:p-7"
          >
            <span class="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-success/15 text-success">
              <Download class="h-7 w-7" />
            </span>
            <div class="flex flex-col gap-1">
              <span class="font-display text-2xl leading-none">{t(msg`Receive`)}</span>
              <span class="text-sm text-muted-foreground">
                {t(msg`Enter a code, grab your files`)}
              </span>
            </div>
          </Link>

          {/* Capa OPCIONAL con cuenta: diferido cifrado + dispositivos de
              confianza. Accesos discretos — el flujo estrella sigue siendo
              el P2P directo sin cuenta. */}
          <div class="flex flex-wrap gap-3 sm:col-span-2 lg:col-span-1 xl:col-span-2">
            <Link
              to="/deferred"
              data-testid="link-deferred"
              class="press inline-flex items-center gap-1.5 rounded-full border border-border bg-surface/60 px-3.5 py-2 text-sm font-medium text-muted-foreground backdrop-blur transition-colors hover:text-foreground"
            >
              <Clock3 class="h-4 w-4" />
              {t(msg`Deferred send`)}
            </Link>
            <Link
              to="/account"
              data-testid="link-account"
              class="press inline-flex items-center gap-1.5 rounded-full border border-border bg-surface/60 px-3.5 py-2 text-sm font-medium text-muted-foreground backdrop-blur transition-colors hover:text-foreground"
            >
              <ShieldCheck class="h-4 w-4" />
              {t(msg`Account & devices`)}
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePage,
});
